import { useState, useEffect } from 'react'
import { useAuth } from '../../Context/AuthContext'
import supabase from '../../supabase'
import { useToast } from '../Invoice/Toast'
import './UserProfile.css'

function UserProfileSetup() {
  const { user, signOut } = useAuth()
  const [profile, setProfile] = useState(null)
  const [bankDetails, setBankDetails] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    address: '',
    gst_number: '',
    logo_url: '',
    email: '',
  })
  const [bankFormData, setBankFormData] = useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch: '',
    upi_id: '',
  })
  const [previewMode, setPreviewMode] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  // Initialize toast hook
  const { showError, showSuccess, showWarning, showInfo, ToastContainer } = useToast()

  useEffect(() => {
    async function fetchUserAndProfile() {
      if (!user) return

      try {
        // Fetch user profile
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single()

        if (error && error.code !== 'PGRST116') {
          showError('Load Error', 'Failed to fetch profile: ' + error.message)
        }

        if (data) {
          setProfile(data)
          setFormData({
            name: data.name || '',
            company_name: data.company_name || '',
            address: data.address || '',
            gst_number: data.gst_number || '',
            logo_url: data.logo_url || '',
            email: data.email || user.email || '',
          })
        } else {
          // New user - set email from auth user
          setFormData((prev) => ({
            ...prev,
            email: user.email,
          }))
        }

        // Fetch bank details
        const { data: bankData, error: bankError } = await supabase
          .from('banks')
          .select('*')
          .eq('user_id', user.id)
          .single()

        if (bankError && bankError.code !== 'PGRST116') {
          showError('Load Error', 'Failed to fetch bank details: ' + bankError.message)
        }

        if (bankData) {
          setBankDetails(bankData)
          setBankFormData({
            bank_name: bankData.bank_name || '',
            account_number: bankData.account_number || '',
            ifsc_code: bankData.ifsc_code || '',
            branch: bankData.branch || '',
            upi_id: bankData.upi_id || '',
          })
        }
      } catch (err) {
        console.error('Unexpected error:', err)
        showError('Unexpected Error', 'An unexpected error occurred while loading data')
      }
    }

    fetchUserAndProfile()
  }, [user])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleBankChange = (e) => {
    const { name, value } = e.target
    setBankFormData({ ...bankFormData, [name]: value })
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file || !user) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Invalid File', 'Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('File Too Large', 'Image size should be less than 5MB')
      return
    }

    setUploading(true)

    try {
      const ext = file.name.split('.').pop()
      const fileName = `${user.id}/logo-${Date.now()}.${ext}`

      const { error: uploadError } = await supabase.storage
        .from('users')
        .upload(fileName, file, { 
          cacheControl: '3600', 
          upsert: true 
        })

      if (uploadError) {
        showError('Upload Error', uploadError.message)
        return
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from('users').getPublicUrl(fileName)

      setFormData((prev) => ({
        ...prev,
        logo_url: publicUrl,
      }))

      showSuccess('Success', 'Logo uploaded successfully!')
    } catch (error) {
      console.error('Upload error:', error)
      showError('Upload Failed', error.message || 'Failed to upload logo')
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      showError('Validation Error', 'Full name is required')
      return
    }

    if (!formData.company_name.trim()) {
      showError('Validation Error', 'Company name is required')
      return
    }

    try {
      const payload = {
        id: user.id,
        name: formData.name,
        company_name: formData.company_name,
        address: formData.address,
        gst_number: formData.gst_number,
        logo_url: formData.logo_url,
        email: formData.email,
      }

      let response
      if (profile) {
        response = await supabase.from('users').update(payload).eq('id', user.id)
      } else {
        response = await supabase.from('users').insert([payload])
      }

      const { error } = response
      if (error) {
        showError('Save Error', error.message || 'Failed to save profile')
        return
      }

      showSuccess('Success', profile ? 'Profile updated successfully!' : 'Profile saved successfully!')
      setProfile(payload)
    } catch (err) {
      console.error('Unexpected error:', err)
      showError('Unexpected Error', 'An unexpected error occurred while saving')
    }
  }

  const handleBankSubmit = async () => {
    // Validation
    if (!bankFormData.bank_name.trim()) {
      showWarning('Validation Warning', 'Bank name is recommended')
    }

    if (!bankFormData.account_number.trim()) {
      showWarning('Validation Warning', 'Account number is recommended')
    }

    try {
      const bankPayload = {
        user_id: user.id,
        bank_name: bankFormData.bank_name,
        account_number: bankFormData.account_number,
        ifsc_code: bankFormData.ifsc_code,
        branch: bankFormData.branch,
        upi_id: bankFormData.upi_id,
      }

      let response
      if (bankDetails) {
        response = await supabase.from('banks').update(bankPayload).eq('user_id', user.id)
      } else {
        response = await supabase.from('banks').insert([bankPayload])
      }

      const { error } = response
      if (error) {
        showError('Save Error', error.message || 'Failed to save bank details')
        return
      }

      showSuccess('Success', bankDetails ? 'Bank details updated successfully!' : 'Bank details saved successfully!')
      setBankDetails(bankPayload)
    } catch (err) {
      console.error('Unexpected error:', err)
      showError('Unexpected Error', 'An unexpected error occurred while saving bank details')
    }
  }

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      const { error } = await signOut()
      if (error) {
        showError('Logout Error', error.message || 'Failed to logout')
      } else {
        showSuccess('Success', 'Logged out successfully')
        // Optional: You can redirect to login page or home page here
        // window.location.href = '/login'
      }
    } catch (error) {
      console.error('Logout error:', error)
      showError('Logout Error', error.message || 'An unexpected error occurred')
    } finally {
      setLoggingOut(false)
    }
  }

  if (!user) return <div className="userprofilepg-loading">Loading...</div>

  return (
    <>
      {/* Toast Container */}
      <ToastContainer />

      <div className="userprofilepg-container">
        <div className="userprofilepg-header">
          <div className="userprofilepg-header-content">
            <div>
              <h1 className="userprofilepg-title">Business Profile</h1>
              <p className="userprofilepg-subtitle">Manage your business information and banking details</p>
            </div>
            <button 
              className="userprofilepg-btn userprofilepg-btn-logout" 
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </div>

        <div className="userprofilepg-content">
          {/* Profile Section */}
          <div className="userprofilepg-section">
            <div className="userprofilepg-section-header">
              <h2 className="userprofilepg-section-title">Business Information</h2>
            </div>
            
            <div className="userprofilepg-profile-grid">
              <div className="userprofilepg-form-fields">
                <div className="userprofilepg-field-group">
                  <label className="userprofilepg-label" htmlFor="name">Full Name *</label>
                  <input
                    id="name"
                    className="userprofilepg-input"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="userprofilepg-field-group">
                  <label className="userprofilepg-label" htmlFor="email">Email Address *</label>
                  <input
                    id="email"
                    className="userprofilepg-input userprofilepg-input-disabled"
                    name="email"
                    value={formData.email}
                    disabled
                  />
                </div>

                <div className="userprofilepg-field-group">
                  <label className="userprofilepg-label" htmlFor="company_name">Company Name *</label>
                  <input
                    id="company_name"
                    className={`userprofilepg-input ${profile?.company_name ? 'userprofilepg-input-disabled' : ''}`}
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Enter your company name"
                    disabled={!!profile?.company_name}
                  />
                  {!profile?.company_name && (
                    <p className="userprofilepg-field-note">
                      Note: Company name can only be set once and cannot be changed later
                    </p>
                  )}
                </div>

                <div className="userprofilepg-field-group">
                  <label className="userprofilepg-label" htmlFor="address">Business Address</label>
                  <textarea
                    id="address"
                    className="userprofilepg-textarea"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter your complete business address"
                    rows="3"
                  />
                </div>

                <div className="userprofilepg-field-group">
                  <label className="userprofilepg-label" htmlFor="gst_number">GST Number</label>
                  <input
                    id="gst_number"
                    className="userprofilepg-input"
                    name="gst_number"
                    value={formData.gst_number}
                    onChange={handleChange}
                    placeholder="Enter GST number (optional)"
                  />
                </div>
              </div>

              <div className="userprofilepg-logo-section">
                <div className="userprofilepg-logo-container">
                  <label className="userprofilepg-label">Company Logo</label>
                  {formData.logo_url ? (
                    <div className="userprofilepg-logo-preview">
                      <img 
                        src={formData.logo_url} 
                        alt="Company Logo" 
                        className="userprofilepg-logo-image"
                      />
                    </div>
                  ) : (
                    <div className="userprofilepg-logo-placeholder">
                      <div className="userprofilepg-logo-icon">📷</div>
                      <span>No logo uploaded</span>
                    </div>
                  )}
                  <div className="userprofilepg-upload-wrapper">
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploading}
                      className="userprofilepg-file-input"
                    />
                    <label htmlFor="logo-upload" className="userprofilepg-upload-btn">
                      {uploading ? 'Uploading...' : 'Upload Logo'}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="userprofilepg-section-actions">
              <button 
                className="userprofilepg-btn userprofilepg-btn-primary" 
                onClick={handleSubmit}
                disabled={uploading}
              >
                {profile ? 'Update Profile' : 'Save Profile'}
              </button>
            </div>
          </div>

          {/* Bank Details Section */}
          <div className="userprofilepg-section">
            <div className="userprofilepg-section-header">
              <h2 className="userprofilepg-section-title">Banking Information</h2>
              <p className="userprofilepg-section-desc">Add your banking details for invoice payments</p>
            </div>

            <div className="userprofilepg-bank-grid">
              <div className="userprofilepg-field-group">
                <label className="userprofilepg-label" htmlFor="bank_name">Bank Name</label>
                <input
                  id="bank_name"
                  className="userprofilepg-input"
                  name="bank_name"
                  value={bankFormData.bank_name}
                  onChange={handleBankChange}
                  placeholder="Enter bank name"
                />
              </div>

              <div className="userprofilepg-field-group">
                <label className="userprofilepg-label" htmlFor="account_number">Account Number</label>
                <input
                  id="account_number"
                  className="userprofilepg-input"
                  name="account_number"
                  value={bankFormData.account_number}
                  onChange={handleBankChange}
                  placeholder="Enter account number"
                />
              </div>

              <div className="userprofilepg-field-group">
                <label className="userprofilepg-label" htmlFor="ifsc_code">IFSC Code</label>
                <input
                  id="ifsc_code"
                  className="userprofilepg-input userprofilepg-input-uppercase"
                  name="ifsc_code"
                  value={bankFormData.ifsc_code}
                  onChange={handleBankChange}
                  placeholder="Enter IFSC code"
                />
              </div>

              <div className="userprofilepg-field-group">
                <label className="userprofilepg-label" htmlFor="branch">Branch Name</label>
                <input
                  id="branch"
                  className="userprofilepg-input"
                  name="branch"
                  value={bankFormData.branch}
                  onChange={handleBankChange}
                  placeholder="Enter branch name"
                />
              </div>

              <div className="userprofilepg-field-group userprofilepg-field-full">
                <label className="userprofilepg-label" htmlFor="upi_id">UPI ID</label>
                <input
                  id="upi_id"
                  className="userprofilepg-input"
                  name="upi_id"
                  value={bankFormData.upi_id}
                  onChange={handleBankChange}
                  placeholder="Enter UPI ID (e.g., yourname@paytm)"
                />
              </div>
            </div>

            <div className="userprofilepg-section-actions">
              <button 
                className="userprofilepg-btn userprofilepg-btn-primary" 
                onClick={handleBankSubmit}
              >
                {bankDetails ? 'Update Bank Details' : 'Save Bank Details'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default UserProfileSetup